import { Button } from '../primitives';
import type { PendingInvitation } from '../../lib/vm/useOrgSwitcherVM';
import type { ShellMembership } from './useShellSession';

export interface RevocationErrorProps {
  revokedOrgName: string;
  memberships: ReadonlyArray<ShellMembership>;
  pendingInvitations: ReadonlyArray<PendingInvitation>;
  onPickOrg: (orgId: string) => void;
  onAcceptInvitation: (token: string) => void;
  onDeclineInvitation: (invitationId: string) => void;
  onSignOut: () => void;
}

/**
 * 17.4b — Full-page error surface shown in place of `<Outlet />` whenever the
 * org-switcher VM has parked a revocation envelope. Two variants:
 *
 * - **With remaining memberships or pending invitations** — heading + body
 *   explaining the revocation, an embedded picker over the non-revoked
 *   memberships (click → fires `beginSwitch` directly, no unsaved-drafts
 *   modal since the active org's content was destroyed at revocation time),
 *   and the pending-invitations section (17.3b.2 shape).
 * - **No remaining memberships and no pending invitations** — alternate copy
 *   pointing at invitations or signup, with a single `[Sign out]` CTA.
 */
export function RevocationError({
  revokedOrgName,
  memberships,
  pendingInvitations,
  onPickOrg,
  onAcceptInvitation,
  onDeclineInvitation,
  onSignOut
}: RevocationErrorProps) {
  const hasRemainingOrgs = memberships.length > 0;
  const hasPendingInvitations = pendingInvitations.length > 0;

  if (!hasRemainingOrgs && !hasPendingInvitations) {
    return (
      <div
        className="pn-revocation pn-revocation-no-orgs"
        data-testid="revocation-error"
        data-variant="no-orgs"
      >
        <h1 className="pn-revocation-heading">
          You're not a member of any Penrose orgs.
        </h1>
        <p className="pn-revocation-body">
          Get an invitation from a teammate, or sign up to start a new org.
        </p>
        <div className="pn-revocation-actions">
          <Button
            variant="primary"
            onClick={onSignOut}
            data-testid="revocation-no-orgs-signout"
          >
            Sign out
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="pn-revocation"
      data-testid="revocation-error"
      data-variant="with-orgs"
    >
      <h1 className="pn-revocation-heading">
        You've been removed from this org
      </h1>
      <p className="pn-revocation-body">
        Your access to <strong>{revokedOrgName}</strong> was revoked. Switch to
        another org to continue.
      </p>

      {hasRemainingOrgs && (
        <div
          className="pn-revocation-picker"
          data-testid="revocation-picker"
        >
          <div className="pn-flyout-eyebrow">Switch to</div>
          {memberships.map((m) => (
            <button
              key={m.org_id}
              type="button"
              className="pn-flyout-row"
              onClick={() => onPickOrg(m.org_id)}
              data-testid="revocation-picker-org"
            >
              <span className="pn-flyout-row-main">
                <span className="pn-flyout-row-name">{m.org_name}</span>
                {m.function_role_name && (
                  <span className="pn-flyout-row-role">
                    {m.function_role_name}
                  </span>
                )}
              </span>
            </button>
          ))}
        </div>
      )}

      {hasPendingInvitations && (
        <div
          className="pn-revocation-invitations"
          data-testid="revocation-pending-invitations"
        >
          <div className="pn-flyout-eyebrow">Pending invitations</div>
          <ul className="pn-flyout-invitations-list">
            {pendingInvitations.map((inv) => (
              <li
                key={inv.id}
                className="pn-flyout-invitation-row"
                data-testid="revocation-pending-invitation"
              >
                <div className="pn-flyout-invitation-meta">
                  <span className="pn-flyout-invitation-name">
                    {inv.org_name}
                  </span>
                  <span className="pn-flyout-invitation-eyebrow">
                    Invited by {inv.inviter_name ?? 'someone on the team'}
                  </span>
                </div>
                <div className="pn-flyout-invitation-actions">
                  <Button
                    variant="ghost"
                    size="sm"
                    data-testid="revocation-pending-invitation-decline"
                    onClick={() => onDeclineInvitation(inv.id)}
                  >
                    Decline
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    data-testid="revocation-pending-invitation-accept"
                    onClick={() => onAcceptInvitation(inv.token)}
                  >
                    Accept
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
